import React, { useRef, useEffect, useState } from 'react'
import styled from 'styled-components'

import Modal, { CloseButton } from '@jarvis.oddle/core/components/Modal'

import { makeControlledModal } from 'src/decorators/ModalController'
import useAppSelector from 'src/hooks/useAppSelector'
import { selectUser } from 'src/redux/user'
import { ODDLE_PASS_REDIRECT_URL } from 'config/client'
import { parseJwt } from 'src/utils/jwtParser'

const StyledIframe = styled.iframe`
  width: 100%;
  height: 80%;
  min-height: calc(100vh - 220px);
  border: 0;
`

interface EInvoiceModalProps {
  modal: {
    show: boolean
    onClose: (handler?) => void
    onClosed: () => void
  }
  storeOrder?: Oms.V4.StoreOrder
}

const EInvoiceModal = ({
  modal,
  storeOrder
}: EInvoiceModalProps): JSX.Element => {
  const user = useAppSelector(selectUser).data
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [isIFrameReady, setIsIframeReady] = useState(false)

  const einvoiceDomain = `https://einvoice${
    ODDLE_PASS_REDIRECT_URL === 'beta.oddle.me' ? '.beta' : ''
  }.oddle.me`

  // Listen for messages from iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Only accept messages from the expected origin
      if (event.data.origin !== einvoiceDomain) {
        return
      }

      // You can handle specific message types here
      if (event.data.status === 'ready') {
        setIsIframeReady(true)
      }
    }

    window.addEventListener('message', handleMessage)

    return () => {
      window.removeEventListener('message', handleMessage)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Send message to iframe when modal opens
  useEffect(() => {
    if (modal.show && iframeRef.current && storeOrder && isIFrameReady) {
      // Wait for iframe to load before sending message
      const iframe = iframeRef.current
      const sendMessageToIframe = () => {
        const accessToken = window.localStorage.getItem('accessToken')
        const {
          state: { data }
        } = parseJwt(accessToken || '') as {
          state: {
            data: {
              email: string
            }
          }
        }
        const message = {
          type: 'order',
          data: {
            orderId: storeOrder.id,
            orderNumber: storeOrder.orderNumber || storeOrder.id,
            customerFirstName: storeOrder.recipientName?.split(' ')[0] || '',
            customerLastName:
              storeOrder.recipientName?.split(' ').slice(1).join(' ') || '',
            email: user?.email || data?.email,
            phoneNumber: storeOrder.recipientContact || '',
            total: storeOrder.total || 0,
            storeId: storeOrder.storeId || '',
            billingAddressOne:
              storeOrder.billingAddress || storeOrder.deliveryAddressOne || '',
            billingAddressTwo:
              storeOrder.billingAddressTwo ||
              storeOrder.deliveryAddressTwo ||
              '',
            billingAddressCity:
              storeOrder.billingAddressCity ||
              storeOrder.deliveryAddressCity ||
              '',
            billingAddressState:
              storeOrder.billingAddressState ||
              storeOrder.deliveryAddressState ||
              '',
            billingAddressPostal:
              storeOrder.billingAddressPostal ||
              storeOrder.deliveryAddressPostal ||
              '',
            storeName: storeOrder.storeName,
            menuName: storeOrder.menuName,
            accessToken
          }
        }

        try {
          iframe.contentWindow?.postMessage(message, einvoiceDomain)
        } catch (error) {
          console.error(
            '❌ EInvoiceModal: Failed to send message to iframe:',
            error
          )
        }
      }

      sendMessageToIframe()
    }
  }, [modal.show, storeOrder, user?.email, isIFrameReady, einvoiceDomain])

  const onClose = () => {
    modal.onClose()
  }

  return (
    <Modal {...modal} slideInAnimation='top'>
      <Modal.Container width='80%'>
        <Modal.Header>
          <CloseButton onClick={onClose} className='e-invoice-close-btn' />
        </Modal.Header>
        <Modal.Body>
          <StyledIframe
            ref={iframeRef}
            src={`${einvoiceDomain}?type=order&id=${storeOrder?.id}`}
            title='E-invoice Request'
          />
        </Modal.Body>
      </Modal.Container>
    </Modal>
  )
}

export default makeControlledModal(EInvoiceModal, 'EInvoiceModal')
